import { ApolloProvider } from '@apollo/client'
import { type ReactNode, useEffect, useRef } from 'react'
import { BrowserRouter, HashRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Analytics } from './analytics/Analytics'
import { ConfigError } from './components/ConfigError'
import { FlowLayout } from './components/FlowLayout'
import { apolloClient } from './graphql/client'
import { useTradeInConfigQuery } from './generated/graphql'
import { LocaleProvider } from './localization/LocaleProvider'
import {
  resolveDefaultLocale,
  SUPPORTED_LOCALES,
  type SupportedLocale,
  toSupportedLocale,
  toSupportedLocales,
} from './localization/types'
import { AddCategoryPage } from './pages/AddCategoryPage'
import { AddManuallyPage } from './pages/AddManuallyPage'
import { ConfirmPage } from './pages/ConfirmPage'
import { DetailsPage } from './pages/DetailsPage'
import { ItemsPage } from './pages/ItemsPage'
import { PublicFrontPage } from './pages/PublicFrontPage'
import { MyTradeInsPage } from './pages/MyTradeInsPage'
import { ShippingPage } from './pages/ShippingPage'
import { YourStoryPage } from './pages/YourStoryPage'
import { AuthProvider } from './providers/AuthProvider'
import { useBootstrap } from './providers/bootstrap'
import {
  RouterMode,
  getRouterBasename,
  getRouterMode,
  resolveRouterBasename,
} from './providers/embedConfig'
import { PortalProvider } from './providers/portal'
import { useAuthStore } from './store/auth'
import { Toast } from './ui/toast'

/**
 * Trade-in v2 — redesigned restricted flow (docs/specs/2026-07-10-tradein-restricted-flow-redesign.md).
 *
 * Provider order:
 *   ApolloProvider  — Core GraphQL client (auth header pulled from the auth store per request)
 *   LocaleProvider  — active language, seeded from the session `locale` (fallback to the host/browser)
 *   AuthProvider    — the protected area: exchanges accessToken → session, gates the flow
 *   FlowRouter      — routing per `tradeInConfig.router` (default MemoryRouter — never touches the host URL)
 *
 * Rendered inside the <tradein-app> shadow root. `.app` establishes the container-query context;
 * `portalContainer` is the shadow-root node (a sibling of the app mount) that overlays / the toast
 * portal into.
 */

interface AppProps {
  portalContainer: HTMLElement
}

/**
 * DEV-only QA override: append `?lang=<locale>` to the dev URL (any of SUPPORTED_LOCALES — da, en,
 * de, sv, fr, nl, nb) to review every screen's texts in that language regardless of the shop's
 * configured languages. Stripped from production builds (guarded by `import.meta.env.DEV`); the real
 * widget's language always follows the storefront.
 */
function devForcedLocale(): SupportedLocale | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null
  const v = new URLSearchParams(window.location.search).get('lang')
  return v && (SUPPORTED_LOCALES as string[]).includes(v) ? (v as SupportedLocale) : null
}

/**
 * Wires the active locale and starts per-shop analytics once Core's public config has loaded. After
 * login, Core's consumer `locale` wins. Before a session exists (the login page), the default follows
 * the shop's configured `ShopLanguages` the same way as v1 — the storefront-detected language if the
 * shop supports it, else the shop's first language (see `resolveDefaultLocale`). `TradeInConfig` is
 * session-less and shared (cache-first) with the login page.
 *
 * The same `ShopLanguages` list is handed to the provider as `shopLocales`, so the language switcher
 * offers the shop's languages rather than every language we bundle.
 */
function LocalizedApp() {
  const { shopId } = useBootstrap()
  const sessionLocale = useAuthStore((s) => s.config.locale)
  const analyticsStarted = useRef(false)
  const { data } = useTradeInConfigQuery({
    variables: { shopId: shopId ?? '' },
    skip: !shopId,
    fetchPolicy: 'cache-first',
  })
  useEffect(() => {
    if (!shopId || !data || analyticsStarted.current) return
    analyticsStarted.current = true
    Analytics.start({
      shopId,
      uaTrackingId: data.UATrackingId,
      gaTrackingId: data.GATrackingId,
    })
  }, [data, shopId])
  const shopLanguages = data?.ShopLanguages?.filter((l): l is string => Boolean(l))
  const initialLocale =
    devForcedLocale() ??
    (sessionLocale ? toSupportedLocale(sessionLocale) : resolveDefaultLocale(shopLanguages))
  const shopLocales = toSupportedLocales(shopLanguages)

  return (
    <LocaleProvider initialLocale={initialLocale} shopLocales={shopLocales}>
      <Gate />
    </LocaleProvider>
  )
}

/** Router future flags — shared across whichever router `tradeInConfig.router` selects. */
const ROUTER_FUTURE = { v7_startTransition: true, v7_relativeSplatPath: true }

/**
 * Wraps the flow's <Routes> in the router chosen by `tradeInConfig.router` (see embedConfig):
 *   Memory  — (default) in-memory, no URL; seeds the flow at `/items`. Isolated — safe for embeds.
 *   Hash    — `#/…` routing; back/forward + deep links without host server routing.
 *   Browser — clean history-API URLs (optional `routerBasename` when not at root).
 * The URL-based routers read `window.location`; an unmatched/root path falls through `*` → `/items`.
 */
function FlowRouter({ children }: { children: ReactNode }) {
  const mode = getRouterMode()
  if (mode === RouterMode.Browser) {
    // Trailing slashes and Shopify Markets locale prefixes ("/da/apps/retrade") are reconciled with
    // the actual path, else React Router renders nothing (see resolveRouterBasename).
    const basename = resolveRouterBasename(getRouterBasename(), window.location.pathname)
    return (
      <BrowserRouter basename={basename} future={ROUTER_FUTURE}>
        {children}
      </BrowserRouter>
    )
  }
  if (mode === RouterMode.Hash) {
    return <HashRouter future={ROUTER_FUTURE}>{children}</HashRouter>
  }
  return (
    <MemoryRouter initialEntries={['/items']} future={ROUTER_FUTURE}>
      {children}
    </MemoryRouter>
  )
}

/**
 * Entry gate (before the session): no shopId → a configuration-error screen; a shopId but no
 * accessToken → the public front page (login); an accessToken → hand off to AuthProvider, which
 * exchanges it for a session and renders the flow.
 */
function Gate() {
  const { shopId, accessToken } = useBootstrap()

  if (!shopId) return <ConfigError />
  if (!accessToken) return <PublicFrontPage shopId={shopId} />

  return (
    <AuthProvider>
      <FlowRouter>
        <Routes>
          <Route element={<FlowLayout />}>
            <Route path="items" element={<ItemsPage />} />
            <Route path="add" element={<AddManuallyPage />} />
            <Route path="category" element={<AddCategoryPage />} />
            <Route path="story" element={<YourStoryPage />} />
            <Route path="details" element={<DetailsPage />} />
            <Route path="shipping" element={<ShippingPage />} />
            <Route path="confirm" element={<ConfirmPage />} />
            <Route path="trade-ins" element={<MyTradeInsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/items" replace />} />
        </Routes>
      </FlowRouter>
    </AuthProvider>
  )
}

export function App({ portalContainer }: AppProps) {
  return (
    <ApolloProvider client={apolloClient}>
      <PortalProvider value={portalContainer}>
        <div className="app">
          <LocalizedApp />
        </div>
        <Toast container={portalContainer} />
      </PortalProvider>
    </ApolloProvider>
  )
}
